import { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { z } from "zod";
import { MiDatePicker, MiDetailFields, MiMap } from "@miplatform/components";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  DATE_TIME_FORMATS,
  ENTRY_TYPE_OPTIONS,
  GARAGE_SIZE_OPTIONS,
  GARAGE_TYPE_OPTIONS,
  HOME_TYPE_OPTIONS,
  INPUT_TYPES,
  PERMISSIONS,
  POWER_TYPE_OPTIONS,
  SWING_DIRECTION_OPTIONS,
  WATER_HEATER_TYPE_OPTIONS,
  regionCodesUS,
} from "@miplatform/constants";
import { useEdit, useMapForm } from "@miplatform/hooks";
import { addToDate, getDate, getStyledFieldSet } from "@miplatform/utils";
import { useCityViewModel } from "@miplatform/view-models";
import { Footer, OptionChip } from "@mibuildweb/components";
import { usePermissions } from "@mibuildweb/hooks";
import { Box, Grid } from "@mui/material";
import { useMap } from "@vis.gl/react-google-maps";
import { CityAutocomplete } from "../../../components/AddHomeForm/CityAutocomplete/CityAutocomplete";
import { ConfigAccordionHeader } from "../../Config/components/ConfigAccordionHeader/ConfigAccordionHeader";

const isValidCoordinate = (coord) => {
  return (
    typeof coord === "number" && Number.isFinite(coord) && !Number.isNaN(coord)
  );
};

export const HomeDetailsTab = ({
  home = {},
  orderTemplateOptions = [],
  isLoading,
  onSubmit = () => {},
  communities,
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const { classes, theme } = useStyles();
  const { hasPermission } = usePermissions();

  const map = useMap();
  const { cities, addCityToState, allCities, getCities } = useCityViewModel();
  const edit = useEdit({ ...home, cityId: home.cityId || "" });
  const lat =
    Number(edit.getValue("latitude")) || home?.communityLocation?.Latitude || 0;
  const lng =
    Number(edit.getValue("longitude")) ||
    home?.communityLocation?.Longitude ||
    0;
  const { handleCoordChange } = useMapForm({
    values: edit.getValue(),
    update: edit.update,
    cities,
    addCity: addCityToState,
    getCities,
    isEditing,
  });

  const canEditHome = hasPermission(PERMISSIONS.HOME.UPDATE);

  useEffect(() => {
    if (map && isValidCoordinate(lat) && isValidCoordinate(lng)) {
      map.panTo({ lat, lng });
    }
  }, [home, map]);

  useEffect(() => {
    if (isEditing && !edit.getValue("isATakeover")) {
      edit.update({ takeoverNote: "" });
    }
  }, [isEditing, edit.getValue("isATakeover")]);

  const addressFieldset = [
    {
      label: "Address Line 1",
      name: "addressLine1",
      fieldType: INPUT_TYPES.TEXTBOX,
      readOnly: !isEditing,
      required: true,
      validationRule: z
        .string()
        .min(1, "Address Line 1 is required")
        .max(46, "Address Line 1 must not exceed 46 characters")
        .regex(
          /^[^:"]*$/,
          "Address line 1 cannot contain colons or double quotes"
        ),
      width: "50%",
    },
    {
      label: "Address Line 2",
      name: "address.addressLine2",
      fieldType: INPUT_TYPES.TEXTBOX,
      readOnly: !isEditing,
      validationRule: z
        .string()
        .max(46, "Address Line 2 must not exceed 46 characters")
        .regex(
          /^[^:"]*$/,
          "Address line 2 cannot contain colons or double quotes"
        ),
      width: "50%",
    },
    {
      label: "City",
      name: "cityId",
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <CityAutocomplete
          isReadOnly={!isEditing}
          value={edit.edits.cityId}
          cities={allCities}
          onChange={(city) => {
            edit.update({
              cityId: city.id,
              city: city.cityName,
              countyName: city.countyName,
              regionCode: city.regionCode,
            });
          }}
          getCities={getCities}
          onCreate={getCities}
        />
      ),
      width: "50%",
    },
    {
      label: "State",
      name: "regionCode",
      fieldType: INPUT_TYPES.SELECT,
      required: true,
      options: regionCodesUS.map((code) => ({ label: code, value: code })),
      value: edit.getValue("regionCode"),
      readOnly: !isEditing,
      validationRule: z.string().length(2, "State must be 2 characters"),
      width: "50%",
    },
    {
      label: "Zip Code",
      name: "postalCode",
      fieldType: INPUT_TYPES.NUMBER,
      required: true,
      readOnly: !isEditing,
      validationRule: z
        .union([z.string(), z.number()], {
          errorMap: () => ({ message: "Zip Code is required" }),
        })
        .transform((val) => String(val))
        .refine((val) => val.length <= 5, {
          message: "Zip Code must not exceed 5 digits",
        })
        .refine((val) => /^\d+$/.test(val), {
          message: "Zip Code must only contain digits (0-9)",
        }),
      width: "50%",
    },
    {
      label: "Is Home Location Precise",
      name: "isLocationPrecise",
      fieldType: INPUT_TYPES.CHECKBOX,
      value: edit.getValue("isLocationPrecise"),
      readOnly: !isEditing,
      width: "50%",
    },
    {
      label: "Latitude",
      name: "latitude",
      fieldType: INPUT_TYPES.NUMBER,
      value: edit.getValue("latitude"),
      readOnly: !isEditing,
      validationRule: z
        .number()
        .or(z.string().transform(Number))
        .refine((val) => val >= -90 && val <= 90, {
          message: "Latitude must be between -90 and 90 degrees",
        }),
      width: "50%",
    },
    {
      label: "Longitude",
      name: "longitude",
      fieldType: INPUT_TYPES.NUMBER,
      value: edit.getValue("longitude"),
      readOnly: !isEditing,
      validationRule: z
        .number()
        .or(z.string().transform(Number))
        .refine((val) => val >= -180 && val <= 180, {
          message: "Longitude must be between -180 and 180 degrees",
        }),
      width: "50%",
    },
    {
      label: "Community Latitude",
      name: "communityLocation.Latitude",
      fieldType: INPUT_TYPES.TEXTBOX,
      value: edit.getValue("communityLocation.Latitude")?.toFixed(5),
      readOnly: true,
      width: "50%",
    },
    {
      label: "Community Longitude",
      name: "communityLocation.Longitude",
      fieldType: INPUT_TYPES.TEXTBOX,
      value: edit.getValue("communityLocation.Longitude")?.toFixed(5),
      readOnly: true,
      width: "50%",
    },
  ];

  const generalFieldset = [
    {
      label: "Builder",
      name: "builder",
      fieldType: INPUT_TYPES.TEXTBOX,
      readOnly: true,
      width: "25%",
    },
    {
      label: "Community",
      name: "communityId",
      fieldType: INPUT_TYPES.SELECT,
      value: edit.getValue("communityId"),
      options: communities.map((community) => ({
        label: community.name,
        value: community.id,
      })),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Home Type",
      name: "homeType",
      fieldType: INPUT_TYPES.SELECT,
      options: HOME_TYPE_OPTIONS,
      value: edit.getValue("homeType"),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Floors",
      name: "floors",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z.coerce
        .string()
        .max(2, "Floors must not exceed 2 digits"),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Square Footage",
      name: "squareFootage",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z.coerce
        .string()
        .max(4, "Square Footage must not exceed 4 digits"),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Power Type",
      name: "powerType",
      fieldType: INPUT_TYPES.SELECT,
      options: POWER_TYPE_OPTIONS,
      value: edit.getValue("powerType"),
      readOnly: !isEditing,
      disabled: isEditing,
      width: "25%",
    },
    {
      label: "Water Heater Type",
      name: "waterHeaterType",
      fieldType: INPUT_TYPES.SELECT,
      options: WATER_HEATER_TYPE_OPTIONS,
      value: edit.getValue("waterHeaterType"),
      readOnly: !isEditing,
      width: "25%",
    },

    {
      label: "Garage Type",
      name: "garageType",
      fieldType: INPUT_TYPES.SELECT,
      options: GARAGE_TYPE_OPTIONS,
      value: edit.getValue("garageType"),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Garage Size",
      name: "garageSize",
      fieldType: INPUT_TYPES.SELECT,
      options: GARAGE_SIZE_OPTIONS,
      value: edit.getValue("garageSize"),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Swing Direction",
      name: "swingDirection",
      fieldType: INPUT_TYPES.SELECT,
      options: SWING_DIRECTION_OPTIONS,
      value: edit.getValue("swingDirection"),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Elevation",
      name: "elevation",
      fieldType: INPUT_TYPES.TEXTBOX,
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Entry Type",
      name: "entryType",
      fieldType: INPUT_TYPES.SELECT,
      options: ENTRY_TYPE_OPTIONS,
      value: edit.getValue("entryType"),
      readOnly: !isEditing,
      width: "25%",
    },

    {
      label: "Baths",
      name: "baths",
      value: "--",
      readOnly: !isEditing,
      disabled: isEditing,
      width: "25%",
    },
    {
      label: "Bedrooms",
      name: "bedrooms",
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z.coerce
        .string()
        .max(4, "Bedrooms must not exceed 4 digits"),
      readOnly: !isEditing,
      width: "25%",
    },
    {
      label: "Job Number",
      name: "jobNumber",
      fieldType: INPUT_TYPES.TEXTBOX,
      readOnly: true,
      width: "50%",
    },
    {
      label: "empty",
      name: "empty1",
      fieldType: INPUT_TYPES.EMPTY_FIELD,
      width: "25%",
    },
    {
      label: "empty",
      name: "empty2",
      fieldType: INPUT_TYPES.EMPTY_FIELD,
      width: "25%",
    },
    {
      label: "empty",
      name: "empty3",
      fieldType: INPUT_TYPES.EMPTY_FIELD,
      width: "60%",
    },
    {
      label: "VIP",
      name: "isVIP",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: !isEditing,
      width: "50px",
    },
    {
      label: "Model Home",
      name: "isModelHome",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: !isEditing,
      width: "50px",
    },
    {
      label: "Basement",
      name: "hasBasement",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: !isEditing,
      width: "50px",
    },
  ];

  const otherFieldset = [
    {
      label: "Closing Date",
      name: "closingDate",
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <MiDatePicker
          label="Closing Date"
          readOnly={!isEditing}
          isFullWidth
          onChange={(date) => {
            edit.update({
              closingDate: date
                ? getDate(date).format(
                    DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
                  )
                : null,
              warrantyEndDate:
                date && home.warrantyPeriodInMonths
                  ? addToDate(
                      date,
                      home.warrantyPeriodInMonths,
                      "months"
                    ).format(DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT)
                  : null,
            });
          }}
          isShrink
          variant={DATEPICKER_VARIANTS.DESKTOP}
          actions={[
            DATEPICKER_ACTIONS.TODAY,
            DATEPICKER_ACTIONS.CLEAR,
            DATEPICKER_ACTIONS.CANCEL,
          ]}
        />
      ),
      width: "25%",
    },
    {
      label: "Warranty End Date",
      name: "warrantyEndDate",
      fieldType: INPUT_TYPES.CUSTOM,
      customComponent: (
        <MiDatePicker
          label="Warranty End Date"
          disabled={!edit.getValue("closingDate")}
          readOnly={!isEditing}
          isFullWidth
          onChange={(date) => {
            edit.update({
              warrantyEndDate: date
                ? getDate(date).format(
                    DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
                  )
                : null,
            });
          }}
          isShrink
          variant={DATEPICKER_VARIANTS.DESKTOP}
          actions={[
            DATEPICKER_ACTIONS.TODAY,
            DATEPICKER_ACTIONS.CLEAR,
            DATEPICKER_ACTIONS.CANCEL,
          ]}
        />
      ),
      width: "25%",
    },
    {
      label: "Year Built",
      name: "yearBuilt",
      readOnly: !isEditing,
      fieldType: INPUT_TYPES.NUMBER,
      validationRule: z.coerce
        .string()
        .max(4, "Year Built must not exceed 4 digits"),
      width: "25%",
    },
    {
      label: "Takeover",
      name: "isATakeover",
      fieldType: INPUT_TYPES.CHECKBOX,
      readOnly: !isEditing,
      width: "25%",
      minWidth: 100,
      flexGrow: 0,
      handleChange: (e) => {
        edit.update({
          isATakeover: e.target.checked,
          ...(!e.target.checked && {
            takeoverStageId: null,
          }),
        });
      },
    },
    {
      label: "Takeover Stage",
      name: "takeoverStageId",
      fieldType: INPUT_TYPES.SELECT,
      renderOption: (option) => <OptionChip {...option} />,
      options: orderTemplateOptions,
      value: edit.getValue("takeoverStageId"),
      width: "100%",
      readOnly: !isEditing || !edit.getValue("isATakeover"),
      required: edit.getValue("isATakeover"),
      sx:
        (isEditing && edit.getValue("isATakeover")) ||
        (!isEditing && home.isATakeover)
          ? { marginTop: theme.spacing(2) }
          : { display: "none" },
    },
    {
      label: "Takeover notes",
      name: "takeoverNote",
      placeholder:
        "Enter the previous company, relevant notes, and any agreements made with the Builder at takeover",
      multiline: true,
      width: "100%",
      rows: 3,
      readOnly: !isEditing || !edit.getValue("isATakeover"),
      sx:
        (isEditing && edit.getValue("isATakeover")) ||
        (!(!isEditing && home.isATakeover) && { display: "none" }),
    },
  ];
  edit.setValidation([
    ...addressFieldset,
    ...generalFieldset,
    ...otherFieldset,
  ]);

  const addressKeys = addressFieldset.map((item) => item.name);
  const locationKeys = ["latitude", "longitude"];

  const checkUpdateAddress = (data, updateKeys) => {
    const updatedHome = { ...data, modifiedOn: home.modifiedOn };
    updateKeys.forEach((key) => {
      if (locationKeys.includes(key)) {
        updatedHome.address = {
          ...updatedHome.address,
          location: {
            ...home.address?.location,
            ...updatedHome.address?.location,
            [key]: data[key],
          },
        };
      } else if (addressKeys.includes(key)) {
        updatedHome.address = {
          ...updatedHome.address,
          [key]: data[key],
        };
      }
    });
    if (updatedHome.address) {
      updatedHome.address = {
        ...home.address,
        ...updatedHome.address,
      };
      if (
        updatedHome.address.location?.latitude === "" ||
        updatedHome.address.location?.longitude === ""
      ) {
        // nullify location object if user clears either lat or long for back-end
        delete updatedHome.address.location;
      }
    }
    return updatedHome;
  };

  const onHandleSubmit = () => {
    const data = edit.edits;
    const keys = Object.keys(data);
    const updatedHome = checkUpdateAddress(data, keys);

    onSubmit(updatedHome);
    handleCancel();
  };

  const handleCancel = () => {
    setIsEditing(false);
    edit.reset();
  };

  const showMarker = isValidCoordinate(lat) && isValidCoordinate(lng);
  const markerPosition = showMarker ? { lat, lng } : null;

  return (
    <>
      <Box className={classes.container}>
        <ConfigAccordionHeader title="Address Information">
          <Grid container spacing={4} paddingTop={4}>
            <Grid item size={{ xs: 12, lg: 8 }}>
              <MiDetailFields
                detailFields={getStyledFieldSet(addressFieldset)}
                edit={edit}
              />
            </Grid>
            <Grid item size={{ xs: 12, lg: 4 }}>
              <MiMap
                enableClick={isEditing}
                zoomFactor={15}
                containerProps={{ className: classes.mapContainer }}
                onCoordChange={handleCoordChange}
                latitudeFieldName="address.location.latitude"
                longitudeFieldName="address.location.longitude"
                showMarker={showMarker}
                initialMarkerPosition={markerPosition}
              />
            </Grid>
          </Grid>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Home Information">
          <Box paddingTop={4}>
            <MiDetailFields
              detailFields={getStyledFieldSet(generalFieldset)}
              edit={edit}
            />
          </Box>
        </ConfigAccordionHeader>
        <ConfigAccordionHeader title="Closing & Warranty Information">
          <Box paddingTop={4}>
            <MiDetailFields
              detailFields={getStyledFieldSet(otherFieldset)}
              edit={edit}
            />
          </Box>
        </ConfigAccordionHeader>
      </Box>
      <Footer
        show
        actions={
          !isEditing && canEditHome
            ? [
                {
                  name: "Edit Home",
                  variant: BUTTON_VARIANTS.OUTLINED,
                  color: BUTTON_COLORS.PRIMARY,
                  onClick: () => setIsEditing(true),
                  disabled: isLoading,
                },
              ]
            : []
        }
        handleCancelClick={handleCancel}
        handleSaveClick={onHandleSubmit}
        disableSave={
          !edit.isValid() ||
          (isEditing &&
            edit.getValue("isATakeover") &&
            !edit.getValue("takeoverStageId")) ||
          isLoading
        }
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    height: "100%",
    overflowY: "auto",
    padding: theme.spacing(4),
    boxSizing: "border-box",
  },
  mapContainer: {
    display: "flex",
    height: 350,
    width: "100%",
    padding: theme.spacing(2),
  },
}));
